// Sidebar grouping helpers for Matrix Spaces.
//
// Spaces (`m.space` rooms) carry `m.space.child` state events naming
// their children. The sidebar renders one section per joined Space
// containing its joined children, plus a CHANNELS bucket for rooms
// with no Space parent. Nested Spaces are flattened to one level.

import type { RoomSummary } from "~/lib/matrix";
import { isDmRoom } from "~/lib/room-utils";

export interface SpaceGroup {
  /** The Space room itself — used for the section header label. */
  space: RoomSummary;
  /** Rooms inside this Space, in the order the caller passed them. */
  children: RoomSummary[];
}

export interface GroupedRooms {
  /** Spaces with at least one visible child, in the order given by
   *  the input `spaces` argument. Empty Spaces are dropped. */
  spaces: SpaceGroup[];
  /** Rooms with no Space parent (or whose only Space parents were
   *  filtered out / not joined). The default CHANNELS bucket. */
  unparented: RoomSummary[];
}

export interface SpaceHierarchy {
  orgSpaces: RoomSummary[];
  childSpacesByOrgSpaceId: Map<string, RoomSummary[]>;
}

export interface RepairedSpaceSelection {
  orgSpaceId: string | null;
  childSpaceId: string | null;
  activeSpaceId: string | null;
}

/**
 * Group `rooms` under `spaces`. A room appears under a Space when the
 * Space lists it in `spaceChildRoomIds` and the caller included it in
 * the `rooms` set.
 *
 * A room belonging to multiple Spaces appears under the first Space
 * (in `spaces` order) that claims it — the v1 layout is one row per
 * room, no duplicates. Rooms unclaimed by any visible Space fall
 * into `unparented`.
 *
 * Pure helper — no React, no SDK. Sidebar tests exercise it directly.
 */
export function groupRoomsBySpace(rooms: RoomSummary[], spaces: RoomSummary[]): GroupedRooms {
  const visibleRooms = new Map(rooms.map((room) => [room.id, room]));
  const claimed = new Set<string>();
  const groups: SpaceGroup[] = [];

  for (const space of spaces) {
    if (!space.isSpace) continue;
    const childIds = space.spaceChildRoomIds ?? [];
    const children: RoomSummary[] = [];
    for (const childId of childIds) {
      if (claimed.has(childId)) continue;
      const room = visibleRooms.get(childId);
      if (!room) continue;
      claimed.add(childId);
      children.push(room);
    }
    if (children.length === 0) continue;
    groups.push({ space, children });
  }

  const unparented = rooms.filter((room) => !claimed.has(room.id));
  return { spaces: groups, unparented };
}

/**
 * Sort: matrix-rust-sdk emits joined rooms in `HashMap`-iteration order,
 * which would otherwise shuffle the sidebar between syncs.
 */
export function sidebarSpaces(rooms: RoomSummary[]): RoomSummary[] {
  return rooms.filter((room) => room.isSpace).sort(compareRoomsByName);
}

export function childSpaces(rooms: RoomSummary[], parentSpaceId: string): RoomSummary[] {
  const parent = rooms.find((r) => r.id === parentSpaceId && r.isSpace);
  if (!parent) return [];
  const childIds = new Set(parent.spaceChildRoomIds ?? []);
  return rooms.filter((r) => r.isSpace && childIds.has(r.id)).sort(compareRoomsByName);
}

export function spaceDestinationOptions(
  rooms: RoomSummary[],
  orgSpaceId: string | null,
): RoomSummary[] {
  if (!orgSpaceId) return sidebarSpaces(rooms);
  const orgSpace = rooms.find((r) => r.id === orgSpaceId && r.isSpace);
  if (!orgSpace) return sidebarSpaces(rooms);
  return [orgSpace, ...childSpaces(rooms, orgSpace.id)];
}

export function deriveSpaceHierarchy(rooms: RoomSummary[]): SpaceHierarchy {
  const spaces = sidebarSpaces(rooms);
  const spaceIds = new Set(spaces.map((space) => space.id));
  const childSpaceIds = new Set<string>();
  for (const space of spaces) {
    for (const childId of space.spaceChildRoomIds ?? []) {
      if (spaceIds.has(childId)) childSpaceIds.add(childId);
    }
  }

  const orgSpaces = spaces.filter((space) => !childSpaceIds.has(space.id));
  const childSpacesByOrgSpaceId = new Map<string, RoomSummary[]>();
  for (const orgSpace of orgSpaces) {
    childSpacesByOrgSpaceId.set(orgSpace.id, childSpaces(rooms, orgSpace.id));
  }
  return { orgSpaces, childSpacesByOrgSpaceId };
}

export function repairSpaceSelection(
  rooms: RoomSummary[],
  selectedOrgSpaceId: string | null,
  selectedChildSpaceId: string | null,
): RepairedSpaceSelection {
  const { orgSpaces, childSpacesByOrgSpaceId } = deriveSpaceHierarchy(rooms);
  const orgExists = selectedOrgSpaceId
    ? orgSpaces.some((space) => space.id === selectedOrgSpaceId)
    : false;
  const orgSpaceId = orgExists ? selectedOrgSpaceId : (orgSpaces[0]?.id ?? null);
  if (!orgSpaceId) return { orgSpaceId: null, childSpaceId: null, activeSpaceId: null };

  const childSpaces = childSpacesByOrgSpaceId.get(orgSpaceId) ?? [];
  const childSpaceId =
    orgExists &&
    selectedChildSpaceId &&
    childSpaces.some((space) => space.id === selectedChildSpaceId)
      ? selectedChildSpaceId
      : null;

  return {
    orgSpaceId,
    childSpaceId,
    activeSpaceId: childSpaceId ?? orgSpaceId,
  };
}

export function visibleRoomIdsForSpace(rooms: RoomSummary[], spaceId: string | null): Set<string> {
  const visible = new Set<string>();
  if (!spaceId) return visible;

  const byId = new Map(rooms.map((room) => [room.id, room]));
  const seenSpaces = new Set<string>();
  const queue = [spaceId];
  let index = 0;

  while (index < queue.length) {
    const currentId = queue[index];
    index += 1;
    if (!currentId || seenSpaces.has(currentId)) continue;
    seenSpaces.add(currentId);
    const space = byId.get(currentId);
    if (!space?.isSpace) continue;
    for (const childId of space.spaceChildRoomIds ?? []) {
      const child = byId.get(childId);
      if (!child) continue;
      if (child.isSpace) queue.push(child.id);
      else visible.add(child.id);
    }
  }

  return visible;
}

function claimedByAnySpace(rooms: RoomSummary[]): Set<string> {
  const claimed = new Set<string>();
  for (const room of rooms) {
    if (!room.isSpace) continue;
    for (const id of room.spaceChildRoomIds ?? []) claimed.add(id);
  }
  return claimed;
}

export function unparentedVisibility(
  rooms: RoomSummary[],
  activeSpaceId: string | null,
): { claimed: Set<string>; show: boolean } {
  const claimed = claimedByAnySpace(rooms);
  if (!activeSpaceId) return { claimed, show: true };
  const active = rooms.find((candidate) => candidate.id === activeSpaceId);
  return { claimed, show: Boolean(active?.isSpace) && !claimed.has(activeSpaceId) };
}

export function visibleRoomsForSpace(rooms: RoomSummary[], spaceId: string | null): RoomSummary[] {
  const ids = visibleRoomIdsForSpace(rooms, spaceId);
  const { claimed, show } = unparentedVisibility(rooms, spaceId);
  return rooms.filter(
    (room) =>
      !room.isSpace && (room.isDirect || ids.has(room.id) || (show && !claimed.has(room.id))),
  );
}

export function isRoomVisibleInSpaceSelection(
  rooms: RoomSummary[],
  activeSpaceId: string | null,
  roomId: string | null,
): boolean {
  if (!roomId) return false;
  if (isDmRoom(rooms, roomId)) return true;
  if (visibleRoomIdsForSpace(rooms, activeSpaceId).has(roomId)) return true;
  return isUnparentedRoomVisible(rooms, activeSpaceId, roomId);
}

export function isUnparentedRoomVisible(
  rooms: RoomSummary[],
  activeSpaceId: string | null,
  roomId: string,
): boolean {
  const room = rooms.find((candidate) => candidate.id === roomId);
  if (!room || room.isSpace || room.isDirect) return false;
  const { claimed, show } = unparentedVisibility(rooms, activeSpaceId);
  return show && !claimed.has(roomId);
}

function compareRoomsByName(a: RoomSummary, b: RoomSummary): number {
  const byName = a.name.localeCompare(b.name);
  if (byName !== 0) return byName;
  return a.id.localeCompare(b.id);
}
